import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// --- ICONS ---
// --- ICONS ---
const FolderIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 6C4 4.89543 4.89543 4 6 4H9.17157C9.70201 4 10.2107 4.21071 10.5858 4.58579L12.4142 6.41421C12.7893 6.78929 13.298 7 13.8284 7H18C19.1046 7 20 7.89543 20 9V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V6Z"
            fill="currentColor" fillOpacity="0.15" />
        <path d="M4 6C4 4.89543 4.89543 4 6 4H9.17157C9.70201 4 10.2107 4.21071 10.5858 4.58579L12.4142 6.41421C12.7893 6.78929 13.298 7 13.8284 7H18C19.1046 7 20 7.89543 20 9V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V6Z"
            className="stroke-[1.5]" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4 10H20" className="stroke-[1.5]" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" opacity="0.4" />
    </svg>
);
const FolderPlusIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 10.5v6m3-3H9m4.06-7.19l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" /></svg>;
const FileIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>;
const ArrowBackIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" /></svg>;
const HomeIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" /></svg>;
const RefreshIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" /></svg>;
const DesktopIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" /></svg>;
const ChevronRightIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>;
const Spinner = () => <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }} className="w-8 h-8 border-4 border-[var(--accent-primary)]/20 border-t-[var(--accent-primary)] rounded-full" />;

/**
 * A local file browser using Tauri's filesystem plugin.
 */
const LocalFileBrowser = ({ onSelect, onCancel, initialPath = '' }) => {
    const [currentPath, setCurrentPath] = useState(initialPath || null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [cache, setCache] = useState({});

    // Well-known starting folders
    const homeDirs = [
        { name: 'Home', path: '/Users', icon: <HomeIcon className="w-4 h-4" /> },
        { name: 'Desktop', path: '~/Desktop', icon: <DesktopIcon className="w-4 h-4" /> },
    ];

    const readDirectory = useCallback(async (dirPath) => {
        if (!dirPath) return;

        if (cache[dirPath]) {
            setItems(cache[dirPath]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        setItems([]); // Clear items immediately

        try {
            const { readDir } = await import('@tauri-apps/plugin-fs');
            const entries = await readDir(dirPath);

            const folderItems = [];
            for (const entry of entries) {
                if (entry.name && entry.name.startsWith('.')) continue;
                if (entry.isDirectory) {
                    const fullPath = `${dirPath}/${entry.name}`.replace(/\/+/g, '/');
                    folderItems.push({ name: entry.name, path: fullPath, type: 'folder' });
                }
            }

            folderItems.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
            setItems(folderItems);
            setCache(prev => ({ ...prev, [dirPath]: folderItems }));
        } catch (err) {
            console.error('Error reading directory:', err);
            setError(`Cannot read this folder: ${err.message || err}`);
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [cache]);

    useEffect(() => {
        if (currentPath) {
            readDirectory(currentPath);
        } else {
            setItems([]);
        }
    }, [currentPath, readDirectory]);

    const handleNavigate = (folderPath) => {
        setCurrentPath(folderPath);
    };

    const handleCreateFolder = async () => {
        if (!currentPath) return;
        const folderName = window.prompt("New folder name:");
        if (!folderName) return;

        try {
            const { mkdir } = await import('@tauri-apps/plugin-fs');
            const newFolderPath = `${currentPath}/${folderName}`.replace(/\/+/g, '/');
            await mkdir(newFolderPath);
            // Clear cache for current path to force refresh
            setCache(prev => {
                const newCache = { ...prev };
                delete newCache[currentPath];
                return newCache;
            });
            readDirectory(currentPath);
        } catch (err) {
            console.error("Failed to create folder:", err);
            alert(`Error creating folder: ${err.message || err}`);
        }
    };

    const handleNativeDialog = async () => {
        try {
            const { open } = await import('@tauri-apps/plugin-dialog');
            const selected = await open({
                directory: true,
                multiple: false,
                title: 'Select a Folder'
            });
            if (selected) {
                onSelect(selected);
            }
        } catch (err) {
            console.error('Dialog error:', err);
        }
    };

    const breadcrumbs = useMemo(() => {
        if (!currentPath) return [{ name: 'Allowed Locations', path: null }];
        const parts = currentPath.split('/').filter(Boolean);
        const crumbs = [{ name: 'Root', path: '/' }];
        let runningPath = '/';

        parts.forEach((part, i) => {
            runningPath += (i > 0 && !runningPath.endsWith('/') ? '/' : '') + part;
            crumbs.push({ name: part, path: runningPath });
        });

        if (crumbs.length > 4) {
            return [crumbs[0], { name: '...', path: null }, ...crumbs.slice(-2)];
        }
        return crumbs;
    }, [currentPath]);

    const containerVariants = {
        initial: { opacity: 0, y: 10 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -10 }
    };

    return (
        <div className="flex flex-col h-full bg-transparent overflow-hidden">
            {/* Toolbar / Breadcrumbs */}
            <div className="flex items-center gap-4 px-8 py-4 border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)]/30 backdrop-blur-md">
                <div className="flex items-center gap-1 overflow-x-auto no-scrollbar flex-1">
                    {breadcrumbs.map((crumb, idx) => (
                        <React.Fragment key={idx}>
                            <button
                                onClick={() => crumb.path && setCurrentPath(crumb.path)}
                                disabled={loading || !crumb.path || crumb.path === currentPath}
                                className={`px-2 py-1 rounded-md text-sm font-medium transition-all ${crumb.path === currentPath
                                    ? 'text-[var(--text-main)] bg-[var(--bg-tertiary)]'
                                    : 'text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-tertiary)]/50'
                                    } ${!crumb.path ? 'cursor-default' : 'cursor-pointer'}`}
                            >
                                {crumb.name}
                            </button>
                            {idx < breadcrumbs.length - 1 && (
                                <ChevronRightIcon className="w-3 h-3 text-[var(--text-muted)]/30 shrink-0" />
                            )}
                        </React.Fragment>
                    ))}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    {currentPath && (
                        <button
                            onClick={handleCreateFolder}
                            disabled={loading}
                            className="p-2 text-[var(--accent-primary)] hover:bg-[var(--accent-primary)]/10 rounded-xl transition-all"
                            title="New Folder"
                        >
                            <FolderPlusIcon className="w-5 h-5" />
                        </button>
                    )}
                    <button
                        onClick={handleNativeDialog}
                        className="p-2 text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-tertiary)] rounded-xl transition-all"
                        title="Native Picker"
                    >
                        <FolderIcon className="w-4 h-4" />
                    </button>
                </div>
            </div>

            <div className="flex flex-1 overflow-hidden">
                {/* Sidebar Shortcuts */}
                <div className="w-52 border-r border-[var(--border-subtle)] bg-[var(--bg-secondary)]/10 px-4 py-8 flex flex-col gap-1.5 shrink-0">
                    <p className="px-3 mb-3 text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest opacity-50">Quick Access</p>
                    {homeDirs.map((dir, i) => (
                        <button
                            key={i}
                            onClick={() => handleNavigate(dir.path)}
                            className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all text-sm font-medium ${currentPath === dir.path ? 'bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] shadow-sm' : 'text-[var(--text-muted)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-main)]'}`}
                        >
                            {dir.icon} {dir.name}
                        </button>
                    ))}
                </div>

                {/* List Area */}
                <div className="flex-1 overflow-y-auto custom-scrollbar relative bg-[var(--bg-main)]/5">
                    <AnimatePresence mode="wait">
                        {loading ? (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[var(--bg-main)]/50 backdrop-blur-[4px]"
                            >
                                <Spinner />
                                <div className="mt-5 text-center space-y-1">
                                    <p className="text-sm font-bold text-[var(--text-main)] animate-pulse">Navigating to new location</p>
                                    <p className="text-[10px] text-[var(--text-muted)] font-bold uppercase tracking-widest opacity-60">Scanning local directory...</p>
                                </div>
                            </motion.div>
                        ) : null}
                    </AnimatePresence>

                    {error && (
                        <div className="m-8 p-5 bg-red-500/5 border border-red-500/20 rounded-3xl text-red-500 text-xs font-semibold text-center shadow-xl shadow-red-500/5">
                            {error}
                        </div>
                    )}

                    {!loading && items.length === 0 && currentPath && !error && (
                        <div className="flex flex-col items-center justify-center h-full text-[var(--text-muted)] opacity-50">
                            <FolderIcon className="w-16 h-16 mb-4 opacity-10" />
                            <p className="text-sm font-medium">Empty Folder</p>
                        </div>
                    )}

                    <motion.div
                        key={currentPath || 'root'}
                        variants={containerVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={{ duration: 0.15 }}
                        className="flex flex-col gap-1 p-6"
                    >
                        {!currentPath && homeDirs.map((dir, idx) => (
                            <button
                                key={idx}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleNavigate(dir.path);
                                }}
                                className="group flex items-center gap-3.5 px-4 py-2 bg-[var(--bg-secondary)]/30 hover:bg-[rgba(var(--accent-primary-rgb),0.06)] border border-[var(--border-strong)]/30 hover:border-[rgba(var(--accent-primary-rgb),0.08)] transition-all text-left rounded-xl shadow-sm hover:shadow-xl hover:shadow-[var(--accent-primary)]/5 focus:outline-none relative"
                            >
                                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[rgba(var(--accent-primary-rgb),0.15)] to-[rgba(var(--accent-primary-rgb),0.03)] border border-[rgba(var(--accent-primary-rgb),0.05)] shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)] group-hover:from-[rgba(var(--accent-primary-rgb),0.25)] group-hover:to-[rgba(var(--accent-primary-rgb),0.08)] flex items-center justify-center shrink-0 transition-all backdrop-blur-sm">
                                    <FolderIcon className="w-4 h-4 text-[var(--accent-primary)] drop-shadow-sm" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-[13px] font-medium text-[var(--text-main)] truncate tracking-tight group-hover:text-[var(--accent-primary)] transition-colors">{dir.name}</h4>
                                </div>
                                <div className="w-7 h-7 rounded-full bg-[var(--bg-tertiary)]/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all -translate-x-3 group-hover:translate-x-0">
                                    <ChevronRightIcon className="w-3 h-3 text-[var(--accent-primary)]" />
                                </div>
                            </button>
                        ))}

                        {currentPath && items.map((item, index) => (
                            <button
                                key={item.path || index}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleNavigate(item.path);
                                }}
                                className="group flex items-center gap-3.5 px-4 py-2 hover:bg-[rgba(var(--accent-primary-rgb),0.06)] border border-transparent hover:border-[rgba(var(--accent-primary-rgb),0.08)] transition-all text-left rounded-xl focus:outline-none relative"
                            >
                                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[rgba(var(--accent-primary-rgb),0.15)] to-[rgba(var(--accent-primary-rgb),0.03)] border border-[rgba(var(--accent-primary-rgb),0.05)] shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)] group-hover:from-[rgba(var(--accent-primary-rgb),0.25)] group-hover:to-[rgba(var(--accent-primary-rgb),0.08)] flex items-center justify-center shrink-0 transition-all backdrop-blur-sm">
                                    <FolderIcon className="w-4 h-4 text-[var(--accent-primary)] drop-shadow-sm" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-[13px] font-medium text-[var(--text-main)] truncate tracking-tight group-hover:text-[var(--accent-primary)] transition-colors">{item.name}</h4>
                                </div>
                                <div className="w-7 h-7 rounded-full bg-[var(--bg-tertiary)]/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all -translate-x-3 group-hover:translate-x-0">
                                    <ChevronRightIcon className="w-3 h-3 text-[var(--accent-primary)]" />
                                </div>
                            </button>
                        ))}
                    </motion.div>
                </div>
            </div>

            {/* Footer */}
            <div className="px-10 py-8 bg-[var(--bg-secondary)]/50 border-t border-[var(--border-subtle)] flex justify-end gap-6 shrink-0 backdrop-blur-xl">
                <button
                    onClick={onCancel}
                    className="px-8 py-3.5 text-sm font-bold text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-tertiary)] rounded-2xl transition-all"
                >
                    Cancel
                </button>
                <button
                    onClick={() => onSelect(currentPath)}
                    disabled={!currentPath}
                    className="px-12 py-3.5 bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] disabled:bg-[var(--bg-tertiary)] disabled:text-[var(--text-muted)]/30 text-slate-950 dark:text-slate-900 text-sm font-black rounded-2xl shadow-2xl shadow-[var(--accent-primary)]/30 transition-all active:scale-95 flex items-center justify-center overflow-hidden relative group"
                >
                    <span className="relative z-10">Select Folder</span>
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
            </div>
        </div>
    );
};

export default LocalFileBrowser;
